import React, { useState, useEffect, useRef, useCallback } from "react";
import "./SuperAdminRoleAssignment.css";
import {
  GETUSERDETAIL_URL,
  GETSECURITYGROUPS_URL,
  SAVEUSERGROUPS_URL,
} from "../../constants";
import axios from "../../datasource/axios";
import { hideLoader } from "../../datasource/msal";
import { Toast } from "primereact/toast";
import helpicon from "../../assets/Help_Icon.svg";
import { Tooltip } from "primereact/tooltip";
import { Dialog } from "primereact/dialog";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Button } from "primereact/button";

const SuperAdminRoleAssignment = (props) => {
  const [search, setSearch] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groups, setGroups] = useState({});
  const [staticGroups, setStaticGroups] = useState([]);
  const [visible, setVisible] = useState(false);
  const toast = useRef(null);
  const [dialogvisible, setDialogVisible] = useState(false);
  const [manualDialogVisible, setManualDialogVisible] = useState(false);
  const [manualUser, setManualUser] = useState({
    userId: "",
    userName: "",
    userEmail: "",
    groups: [],
  });

  useEffect(() => {
    getSecurityGroups();
  }, []);

  const getSecurityGroups = async () => {
    let url = process.env.REACT_APP_API_URI + GETSECURITYGROUPS_URL;

    try {
      const res = await axios.getRequest(url);
      hideLoader();
      setStaticGroups(res?.data);
      resetGroups(res?.data);
    } catch (error) {
      console.error("Error fetching security groups:", error);
    }
  };

  const resetGroups = (groupNames = staticGroups) => {
    const initialGroups = {};
    groupNames.forEach((group) => {
      initialGroups[group] = [];
    });
    setGroups(initialGroups);
  };

  const fetchUser = async () => {
    if (!search.trim()) {
      setSelectedUsers([]);
      resetGroups();
      return;
    }

    const email = search.trim();
    const domain = email.split("@")[1]?.split(".")[0];
    if (
      domain.toString().toLocaleLowerCase().trim() !== "ttecdigital" &&
      domain.toString().toLocaleLowerCase().trim() !== "percepta"
    ) {
      toast.current.show({
        severity: "error",
        summary: "Invalid Email",
        detail:
          "Please enter an email with the domain 'ttecdigital' or 'percepta'.",
        life: 4000,
      });
      return;
    }

    let url =
      process.env.REACT_APP_API_URI + GETUSERDETAIL_URL + "?mail=" + search;

    try {
      const res = await axios.getRequest(url);
      hideLoader();
      if (res?.data) {
        const userExists = selectedUsers.some(
          (user) => user.userId === res.data.userId
        );
        if (!userExists) {
          setSelectedUsers((prev) => [...prev, res.data]);
          populateUserGroups(res?.data);
          toast.current?.show({
            severity: "success",
            summary: "User Found",
            closable: true,
          });
        } else {
          toast.current?.show({
            severity: "info",
            summary: "User Already Selected",
            detail: "This user is already added to the selected users.",
            life: 3000,
          });
        }
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "User Not Found",
          life: 4000,
        });
      }
    } catch (error) {
      console.error("Error fetching user:", error);
    }
  };

  const populateUserGroups = (userData) => {
    const updatedGroups = { ...groups };

    staticGroups.forEach((group) => {
      if (userData.groups?.includes(group)) {
        if (!updatedGroups[group]) {
          updatedGroups[group] = [];
        }

        const userExists = updatedGroups[group].some(
          (u) => u.userId === userData.userId
        );

        if (!userExists) {
          updatedGroups[group].push({ ...userData, active: true });
        }
      }
    });

    setGroups(updatedGroups);
  };

  const assignUserToGroup = useCallback((group, user) => {
    if (!selectedUsers.length) return;

    setGroups((prev) => {
      const updatedGroups = { ...prev };

      const updatedGroup = updatedGroups[group] || [];

      const userExists = updatedGroup.some((u) => u.userId === user.userId);

      if (!userExists) {
        updatedGroup.push({ ...user, active: true });
      } else {
        updatedGroup.forEach((u) => {
          if (u.userId === user.userId) {
            u.active = true;
          }
        });

        updatedGroups[group] = updatedGroups[group].map((u) =>
          u.userId === user.userId ? u : u
        );
      }

      updatedGroups[group] = updatedGroup;
      return updatedGroups;
    });
  });

  const toggleUserGroupStatus = (user, group) => {
    setGroups((prev) => ({
      ...prev,
      [group]: prev[group].map((u) =>
        u.userId === user.userId ? { ...u, active: !u.active } : u
      ),
    }));
  };

  const saveAssignments = async () => {
    if (selectedUsers.length === 0) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Please select at least one user",
        life: 4000,
      });
      return;
    }

    // const userGroupsToSave = Object.keys(groups).filter(group => groups[group].some(user => user.active));

    // if (userGroupsToSave.length === 0) {
    //     toast.current.show({
    //         severity: "error",
    //         summary: "Error",
    //         detail: "Please add users to at least one group",
    //         life: 4000,
    //     });
    //     return;
    // }

    const payload = selectedUsers.map((user) => ({
      userId: user.userId,
      userName: user.userName,
      userEmail: user.userEmail,
      groups: Object.keys(groups).filter((group) =>
        groups[group].some((u) => u.userId === user.userId && u.active)
      ),
    }));

    const hasEmptyGroups = payload.some((user) => user.groups.length === 0);

    if (hasEmptyGroups) {
      setDialogVisible(true);
    } else {
      proceedWithSave(payload);
    }
  };

  const proceedWithSave = async (payload) => {
    let BASE_URL = process.env.REACT_APP_API_URI;
    let url = BASE_URL + SAVEUSERGROUPS_URL;

    try {
      const res = await axios.postRequest(url, payload);
      const userExists = selectedUsers.some(
        (user) => user.userName === props.username
      );
      if (userExists) {
        setVisible(true);
      }
      hideLoader();
      setSearch("");
      setSelectedUsers([]);
      resetGroups();
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "User assignments saved successfully",
        life: 4000,
      });
    } catch (error) {
      console.error("Error saving assignments:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to save user assignments",
        life: 4000,
      });
    }
  };

  const handleConfirm = () => {
    setVisible(false);
    window.location.reload();
  };

  const headerElement = (
    <div className="inline-flex align-items-center justify-content-center gap-2">
      <span className="font-bold white-space-nowrap">Confirmation</span>
    </div>
  );

  const footerContent = (
    <div>
      <Button label="Ok" icon="pi pi-check" onClick={handleConfirm} autoFocus />
    </div>
  );

  const handleYes = () => {
    setDialogVisible(false);
    const payload = selectedUsers.map((user) => ({
      userId: user.userId,
      userName: user.userName,
      userEmail: user.userEmail,
      groups: Object.keys(groups).filter((group) =>
        groups[group].some((u) => u.userId === user.userId && u.active)
      ),
    }));
    proceedWithSave(payload);
  };

  const handleNo = () => {
    setDialogVisible(false);
  };

  const handleManualUserAdd = () => {
    const { userId, userName, userEmail, groups: selectedGroups } = manualUser;
  
    // Validation: all fields are required
    if (!userId || !userName || !userEmail || selectedGroups.length === 0) {
      toast.current.show({
        severity: "error",
        summary: "Missing Required Fields",
        detail: "Please enter User Id, Name, Email, and select at least one group.",
        life: 4000,
      });
      return;
    }
  
    const userAlreadyExists = selectedUsers.some(user => user.userId === userId);
    if (userAlreadyExists) {
      toast.current.show({
        severity: "warn",
        summary: "Duplicate User",
        detail: "A user with this ID has already been added.",
        life: 3000,
      });
      return;
    }
  
    const newUser = {
      userId,
      userName,
      userEmail,
      groups: selectedGroups,
    };
  
    setSelectedUsers((prev) => [...prev, newUser]);
  
    const updatedGroups = { ...groups };
    selectedGroups.forEach((group) => {
      if (!updatedGroups[group]) updatedGroups[group] = [];
      updatedGroups[group].push({ ...newUser, active: true });
    });
  
    setGroups(updatedGroups);
  
    setManualUser({ userId: "", userName: "", userEmail: "", groups: [] });
    setManualDialogVisible(false);
  
    toast.current.show({
      severity: "success",
      summary: "User Added",
      detail: "Manual user added successfully.",
      life: 3000,
    });
  };
  

  return (
    <div className="container">
      <Toast ref={toast} />
      <Dialog
        header="Add User Manually"
        visible={manualDialogVisible}
        style={{ width: "30vw" }}
        onHide={() => setManualDialogVisible(false)}
        modal
      >
        <div className="p-fluid">
          <label htmlFor="userId">Id</label>
          <input
            id="userId"
            type="text"
            className="p-inputtext"
            value={manualUser.userId}
            onChange={(e) =>
              setManualUser({ ...manualUser, userId: e.target.value })
            }
          />
          </div>

        <div className="p-fluid">
          <label htmlFor="userName">Name</label>
          <input
            id="userName"
            type="text"
            className="p-inputtext"
            value={manualUser.userName}
            onChange={(e) =>
              setManualUser({ ...manualUser, userName: e.target.value })
            }
          />

          <label htmlFor="userEmail" style={{ marginTop: "1rem" }}>
            Email
          </label>
          <input
            id="userEmail"
            type="email"
            className="p-inputtext"
            value={manualUser.userEmail}
            onChange={(e) =>
              setManualUser({ ...manualUser, userEmail: e.target.value })
            }
          />

          <label htmlFor="userGroups" style={{ marginTop: "1rem" }}>
            Groups
          </label>
          <select
            id="userGroups"
            multiple
            className="p-multiselect"
            value={manualUser.groups}
            onChange={(e) =>
              setManualUser({
                ...manualUser,
                groups: Array.from(
                  e.target.selectedOptions,
                  (option) => option.value
                ),
              })
            }
            style={{ width: "100%", height: "6rem" }}
          >
            {staticGroups.map((group) => (
              <option key={group} value={group}>
                {group}
              </option>
            ))}
          </select>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "1rem",
            }}
          >
            <Button
              label="Add"
              className="p-button-sm p-button-primary"
              onClick={handleManualUserAdd}
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={visible}
        className="custom-dialog"
        closable={false}
        modal
        header={headerElement}
        footer={footerContent}
        style={{ width: "50rem" }}
        onHide={() => {
          if (!visible) return;
          setVisible(false);
        }}
      >
        <p className="m-0">
          You have modified your own groups. Please click on <b>Ok</b> to see
          the changes.
        </p>
      </Dialog>
      <ConfirmDialog
        visible={dialogvisible}
        onHide={() => setDialogVisible(false)}
        message={
          <span>
            There are users with empty groups. The system will assume that
            either all groups have been revoked for the specific user or no
            groups have been assigned intentionally. Please click <b>Yes</b> to
            proceed.
          </span>
        }
        header="Confirmation"
        icon="pi pi-exclamation-triangle"
        accept={handleYes}
        reject={handleNo}
        acceptLabel="Yes"
        rejectLabel="No"
        closable={false}
        className="custom-confirm-dialog"
      />
      <h2 className="title">User Role Management</h2>
      <div>
        <button
          className="manual-add-button"
          onClick={() => setManualDialogVisible(true)}
        >
          Add User Manually
        </button>
      </div>
      <div className="grid">
        <div className="user-card">
          <div className="div-user">
            <h3 className="hdr-user">Users</h3>
            &nbsp;
            <Tooltip target=".custom-target-icon" />
            <i
              className="custom-target-icon"
              data-pr-position="right"
              data-pr-at="right+5 top"
              data-pr-my="left center-2"
              data-pr-tooltip="Please enter the email of the user you want to assign groups. Multiple users can be selected to assign the groups."
              style={{ fontSize: "1.5rem", cursor: "pointer", height: "5rem" }}
            >
              <img src={helpicon} alt="Help" />
            </i>
          </div>
          <div className="search-container">
            <input
              type="text"
              placeholder="Please Enter Email"
              className="search-input"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                // if (!e.target.value.trim()) {
                //     setSelectedUsers([]);
                //     resetGroups();
                // }
              }}
            />
            <button className="search-button" onClick={fetchUser}>
              Search
            </button>
          </div>
          {selectedUsers.length > 0 && (
            <div className="user-item">
              <p>Selected Users:</p>
              {selectedUsers.map((user) => (
                <div key={user.userId} className={`div-${user.userId}`}>
                  <p>{user.userName}</p>
                  <div className={`group-buttons-${user.userId}`}>
                    {staticGroups.map((group) => (
                      <button
                        key={group}
                        title="Click to assign this group to the user"
                        className={`group-button ${
                          groups[group]?.some(
                            (u) => u.userId === user.userId && u.active
                          )
                            ? "assigned"
                            : ""
                        }`}
                        onClick={() => assignUserToGroup(group, user)}
                      >
                        {group}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {staticGroups.map((group) => (
          <div
            key={group}
            className="group-card wider-group"
            title="Click to remove assigned group for the user"
          >
            <h3>{group}</h3>
            {groups[group]
              ?.filter((user) => user.active)
              .map((user) => (
                <div key={user.userId} className="assigned-user-container">
                  <button
                    className="assigned-user active"
                    onClick={() => toggleUserGroupStatus(user, group)}
                  >
                    {user.userName}
                  </button>
                </div>
              ))}
          </div>
        ))}
      </div>
      <button onClick={saveAssignments} className="save-button">
        Save Assignments
      </button>
    </div>
  );
};

export default SuperAdminRoleAssignment;
